import { useTranslation } from '@/lib/i18n';
import type { FundingDashboard, FundingDashboardBucket } from '@/lib/api-admin/types';

/** bucketAt(UTC ISO) → 축 라벨. TODAY=시각(HH시), 그 외=날짜(M/D). */
const bucketLabel = (bucketAt: string, isHourly: boolean): string => {
  const d = new Date(bucketAt);
  if (Number.isNaN(d.getTime())) return '';
  if (isHourly) return `${d.getHours()}시`;
  return `${d.getMonth() + 1}/${d.getDate()}`;
};

const formatAmount = (amount: number): string => new Intl.NumberFormat('ko-KR').format(amount);

export interface FundingDashboardChartProps {
  data: FundingDashboard;
}

/**
 * #2 펀딩 모니터링 시계열 막대 그래프 — bucket 별 paidAmount 를 CSS 막대로 렌더.
 * 차트 라이브러리 의존 없이 토큰(--chart-primary) 기반. range=TODAY 면 시각 축, 그 외 날짜 축.
 * ADR-031 §1.3 / backoffice-design-reference §12 이미지 #7 정합.
 */
export const FundingDashboardChart = ({ data }: FundingDashboardChartProps) => {
  const { t } = useTranslation('funding');
  const buckets: ReadonlyArray<FundingDashboardBucket> = data.buckets ?? [];
  const isHourly = data.range === 'TODAY';

  const maxAmount = buckets.reduce((max, b) => Math.max(max, b.paidAmount?.amount ?? 0), 0);

  return (
    <section
      aria-label={t('funding.dashboard.chartTitle')}
      className="rounded-lg p-5"
      style={{ background: 'var(--surface-raised)', boxShadow: 'var(--shadow-card)' }}
      data-testid="funding-dashboard-chart"
    >
      <div className="mb-3 flex items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-[color:var(--content-strong)]">
          {t('funding.dashboard.chartTitle')}
        </h3>
        <span className="text-xs text-[color:var(--content-muted)]">
          {t('funding.dashboard.chartCaption', {
            from: new Date(data.rangeFrom).toLocaleDateString('ko-KR'),
            to: new Date(data.rangeTo).toLocaleDateString('ko-KR'),
          })}
        </span>
      </div>

      {buckets.length === 0 || maxAmount === 0 ? (
        <p className="py-8 text-center text-sm text-[color:var(--content-muted)]">
          {t('funding.dashboard.chartEmpty')}
        </p>
      ) : (
        <div
          className="flex h-[200px] items-end gap-1 overflow-x-auto"
          role="img"
          aria-label={t('funding.dashboard.chartTitle')}
        >
          {buckets.map((b) => {
            const amount = b.paidAmount?.amount ?? 0;
            const heightPct = maxAmount > 0 ? Math.max(2, Math.round((amount / maxAmount) * 100)) : 0;
            const label = bucketLabel(b.bucketAt, isHourly);
            return (
              <div
                key={b.bucketAt}
                className="flex min-w-[24px] flex-1 flex-col items-center justify-end gap-1"
                data-testid="funding-dashboard-bar"
                title={`${label} · ${formatAmount(amount)} (${b.pledgeCount}건)`}
              >
                <div
                  className="w-full rounded-t-sm"
                  style={{
                    height: `${heightPct}%`,
                    background: 'var(--chart-primary)',
                    minHeight: amount > 0 ? '2px' : '0',
                  }}
                />
                <span className="truncate text-[10px] text-[color:var(--content-muted)]">{label}</span>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};
